/**
 * RdsCompactRow — compact RDS instance row for Stack View.
 *
 * Maps the AWS `dbStatus` string to a HealthStatus for display via the
 * `mapRdsStatusToHealth` pure function (BR-U5-05).
 */
import type { RdsInstance, HealthStatus } from '@/types';
import { StatusBadge } from '../primitives/StatusBadge';
import styles from './RdsCompactRow.module.css';

/**
 * Maps an AWS RDS dbStatus string to a HealthStatus value (BR-U5-05).
 * Returns a valid HealthStatus for every possible input string (PBT-03 invariant).
 */
export function mapRdsStatusToHealth(dbStatus: string): HealthStatus {
  switch (dbStatus.toLowerCase()) {
    case 'available':
      return 'HEALTHY';
    case 'stopped':
    case 'stopping':
    case 'inaccessible-encryption-credentials':
    case 'incompatible-restore':
    case 'incompatible-network':
      return 'DOWN';
    case 'starting':
    case 'rebooting':
    case 'modifying':
    case 'upgrading':
    case 'creating':
    case 'deleting':
    case 'backing-up':
    case 'configuring-enhanced-monitoring':
    case 'configuring-iam-database-auth':
    case 'configuring-log-exports':
    case 'converting-to-vpc':
    case 'maintenance':
    case 'renaming':
    case 'resetting-master-credentials':
    case 'restore-error':
    case 'storage-optimization':
    case 'storage-full':
    case 'upgrading-storage':
      return 'UNHEALTHY';
    default:
      return 'UNKNOWN';
  }
}

export interface RdsCompactRowProps {
  instance: RdsInstance;
}

export function RdsCompactRow({ instance }: RdsCompactRowProps) {
  const health = mapRdsStatusToHealth(instance.dbStatus);

  return (
    <div
      className={styles.row}
      aria-label={`${instance.dbIdentifier} — ${health}`}
    >
      <StatusBadge status={health} size="sm" />
      <span className={styles.id} title={instance.dbIdentifier}>
        {instance.dbIdentifier}
      </span>
      <span className={styles.engine}>
        {instance.engine} {instance.engineVersion}
      </span>
      <span className={styles.status}>{instance.dbStatus}</span>
    </div>
  );
}
